// Components
// Libs
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// API
import getDefaultCart from '@/common/api/carts/getDefaultCart';
import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { ENDPOINT_DEFAULT_CART, ROUTE_SCANBAT } from '@/common/constants';
// Hooks
import useDefaultCart from '@/common/hooks/carts/useDefaultCart';

const DefaultCartContext = createContext(null);

export const DefaultCartContextProvider = ({ children, isLoggedIn }) => {
  const { t } = useTranslation();

  const isScanbat = window.location.pathname.includes(ROUTE_SCANBAT);

  const { data: dataDefaultCart, refetch: refetchDefaultCart } = useDefaultCart(
    {
      isLoggedIn,
      source: isScanbat ? 'scanbat' : null,
    },
  );

  const [defaultCart, setDefaultCart] = useState(null);

  useEffect(() => {
    setDefaultCart(dataDefaultCart);
  }, [dataDefaultCart]);

  const {
    mutate: mutateGetDefaultCart,
    isLoading: isLoadingGetDefaultCart,
    isSuccess: isSuccessGetDefaultCart,
  } = useMutation(getDefaultCart);

  /**
   * Get default cart to the onSuccess
   * @param onSuccess (func) : function to call if success
   * @type {(function(*=): void)|*}
   */
  const onGetDefaultCart = useCallback(
    (onSuccess = () => {}) => {
      mutateGetDefaultCart(
        { queryKey: [ENDPOINT_DEFAULT_CART] },
        {
          onSuccess,
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutateGetDefaultCart, t],
  );

  return (
    <DefaultCartContext.Provider
      value={{
        defaultCart,
        isLoadingGetDefaultCart,
        isSuccessGetDefaultCart,
        onGetDefaultCart,
        refetchDefaultCart,
      }}
    >
      {children}
    </DefaultCartContext.Provider>
  );
};

export const useDefaultCartContext = () => useContext(DefaultCartContext);

DefaultCartContextProvider.defaultProps = {
  isLoggedIn: false,
};

DefaultCartContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  isLoggedIn: PropTypes.bool,
};
